import * as Sentry from '@sentry/react'
import LogRocket from 'logrocket'
import setupLogRocketReact from 'logrocket-react'
import mixpanel from 'mixpanel-browser'
import { onCLS, onFCP, onFID, onLCP, onTTFB } from 'web-vitals'

import { constants, isProduction, isStaging } from '../constants'
import { getGtag, getPageLocation, getPagePath, getPageTitle } from '../dom'
import { isNotBot } from '../env'
import { AnalyticsProviders, getFakeProviders, ILogRocket } from './shared'

const gtag = getGtag()

let gaMeasurementId: null | string = null

type Providers = AnalyticsProviders<ILogRocket<Parameters<typeof LogRocket.init>[1]>, typeof Sentry>
// type Providers = AnalyticsProviders<typeof fake.logRocket, typeof Sentry>

const enableGoogleAnalytics = isProduction && isNotBot
const enableLogRocket = (isProduction || isStaging) && isNotBot
const enableMixpanel = isNotBot
const enableSentry = (isProduction || isStaging) && isNotBot
const enableVitals = isProduction && isNotBot

const fake = getFakeProviders(Sentry)

const real: Omit<Providers, 'oneSignal'> = {
  googleAnalytics: {
    init: async measurementId => {
      gaMeasurementId = measurementId
    },
    identify: async userId => {
      if (gaMeasurementId) {
        gtag('config', gaMeasurementId, { user_id: userId })
      }
    },
    track: async (eventName, properties) => {
      gtag('event', eventName, properties)
    },
    trackScreen: async () => {
      if (gaMeasurementId) {
        gtag('config', gaMeasurementId, {
          page_title: getPageTitle(),
          page_location: getPageLocation(),
          page_path: getPagePath()
        })
      }
    }
  },
  initVitals: callback => {
    onCLS(callback)
    onFID(callback)
    onFCP(callback)
    onLCP(callback)
    onTTFB(callback)
  },
  // disabled LogRocket for now for performance
  // fake.logRocket,
  logRocket: {
    init: async (appID, options) => {
      LogRocket.init(appID, options)

      // attach logrocket session to analytics
      LogRocket.getSessionURL(sessionURL => {
        if (enableSentry) {
          Sentry.configureScope(scope => {
            scope.setExtra('sessionURL', sessionURL)
          })
        }
      })

      setupLogRocketReact(LogRocket)
    },
    identify: async userId => LogRocket.identify(userId),
    track: async eventName => LogRocket.track(eventName),
    trackScreen: () => providers.logRocket.track(`viewedpage:${getPagePath()}`)
  },
  mixpanel: {
    init: async token => {
      mixpanel.init(token, { api_host: `${constants.apiServerUrl}/a` })
    },
    identify: userId => {
      mixpanel.identify(userId)
    },
    setUserProperty: (property, value) => {
      mixpanel.people.set(property, value)
    },
    setUserPropertyOnce: (property, value) => {
      mixpanel.people.set_once(property, value)
    },
    track: async (eventName, properties) => {
      mixpanel.track(eventName, properties)
    },
    reset: () => {
      mixpanel.reset()
    }
  },
  sentry: {
    init: () => {
      Sentry.init({
        dsn: constants.sentryDsn,
        release: constants.sentryRelease,
        environment: constants.sentryEnvironment,
        tracesSampleRate: 0.05 // 5% of transactions will be recorded and sent to Sentry
        /*
          @todo find an equivalent integration with react-navigation
          integrations: [
            new Integrations.BrowserTracing({
              routingInstrumentation: Sentry.reactRouterV5Instrumentation(history)
            })
          ]
        */
      })

      return Sentry
    },
    identify: userId => {
      Sentry.setUser({ id: userId })
    },
    reset: () => {
      Sentry.setUser(null)
    }
  }
}

export const providers: Providers = {
  googleAnalytics: enableGoogleAnalytics ? real.googleAnalytics : fake.googleAnalytics,
  initVitals: enableVitals ? real.initVitals : fake.initVitals,
  logRocket: enableLogRocket ? real.logRocket : fake.logRocket,
  mixpanel: enableMixpanel ? real.mixpanel : fake.mixpanel,
  oneSignal: fake.oneSignal,
  sentry: enableSentry ? real.sentry : fake.sentry
}
